import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TruckIcon,
  BuildingOfficeIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

function LogisticsDashboard() {
  const [selectedTimeframe, setSelectedTimeframe] = useState('7d');
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');

  const metrics = [
    {
      name: 'Active Shipments',
      value: '12',
      change: '+2',
      changeType: 'increase',
      icon: TruckIcon
    },
    {
      name: 'Active 3PLs',
      value: '4',
      change: '0',
      changeType: 'neutral',
      icon: BuildingOfficeIcon
    },
    {
      name: 'On-Time Delivery',
      value: '96%',
      change: '+3%',
      changeType: 'increase',
      icon: ClockIcon
    },
    {
      name: 'Inventory Accuracy',
      value: '99.2%',
      change: '+0.5%',
      changeType: 'increase',
      icon: CheckCircleIcon
    }
  ];

  const recentShipments = [
    {
      id: 'SHP-001',
      origin: 'Shenzhen, China',
      destination: 'Dubai 3PL Hub',
      status: 'In Transit',
      eta: '2024-03-25',
      items: 2,
      totalUnits: 1500
    },
    {
      id: 'SHP-002',
      origin: 'Mumbai, India',
      destination: 'Riyadh 3PL Hub',
      status: 'Customs Clearance',
      eta: '2024-03-22',
      items: 1,
      totalUnits: 800
    },
    {
      id: 'SHP-003',
      origin: 'Istanbul, Turkey',
      destination: 'Cairo 3PL Hub',
      status: 'Scheduled',
      eta: '2024-03-28',
      items: 2,
      totalUnits: 3200
    }
  ];

  const alerts = [
    {
      type: 'warning',
      message: 'Low inventory alert for SKU WH-001 at Dubai 3PL Hub',
      action: 'Review'
    },
    {
      type: 'info',
      message: 'New shipment SHP-004 scheduled for next week',
      action: 'View'
    },
    {
      type: 'warning',
      message: 'Delayed customs clearance for SHP-002',
      action: 'Track'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'Scheduled': 'bg-blue-100 text-blue-800',
      'In Transit': 'bg-yellow-100 text-yellow-800',
      'Customs Clearance': 'bg-indigo-100 text-indigo-800',
      'Delivered': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Logistics Overview</h1>
        <p className="mt-1 text-sm text-gray-500">
          Monitor your inbound shipments and 3PL operations
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex space-x-4">
        <select
          value={selectedTimeframe}
          onChange={(e) => setSelectedTimeframe(e.target.value)}
          className="rounded-lg border-gray-300 text-sm"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>

        <select
          value={selectedWarehouse}
          onChange={(e) => setSelectedWarehouse(e.target.value)}
          className="rounded-lg border-gray-300 text-sm"
        >
          <option value="all">All Warehouses</option>
          <option value="dubai">Dubai 3PL Hub</option>
          <option value="riyadh">Riyadh 3PL Hub</option>
          <option value="cairo">Cairo 3PL Hub</option>
        </select>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {metrics.map((metric) => (
          <div
            key={metric.name}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-[#0049ac]/10 rounded-lg">
                  <metric.icon className="h-6 w-6 text-[#0049ac]" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                  <p className="text-2xl font-semibold text-gray-900">{metric.value}</p>
                </div>
              </div>
              {metric.change && (
                <div className={`flex items-center ${
                  metric.changeType === 'increase' ? 'text-green-600' : 
                  metric.changeType === 'decrease' ? 'text-red-600' : 
                  'text-gray-600'
                }`}>
                  <ArrowTrendingUpIcon className={`h-4 w-4 mr-1 ${
                    metric.changeType === 'decrease' ? 'rotate-180' : ''
                  }`} />
                  <span className="text-sm font-medium">{metric.change}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Shipments */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Recent Shipments</h2>
                <Link
                  to="/inbound"
                  className="text-sm font-medium text-[#0049ac] hover:text-[#0049ac]/90"
                >
                  View All
                </Link>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Origin</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ETA</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentShipments.map((shipment) => (
                    <tr key={shipment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {shipment.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {shipment.origin}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {shipment.destination}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(shipment.status)}`}>
                          {shipment.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {shipment.items} ({shipment.totalUnits} units)
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {shipment.eta}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div>
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Alerts</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {alerts.map((alert, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50"
                  >
                    <div className="flex-shrink-0">
                      {alert.type === 'warning' ? (
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                      ) : (
                        <CheckCircleIcon className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{alert.message}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <button className="text-sm font-medium text-[#0049ac] hover:text-[#0049ac]/90">
                        {alert.action}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LogisticsDashboard;